import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Participant, Percurso } from 'shared';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SortField =
  | 'nome'
  | 'apelido'
  | 'data_nascimento'
  | 'email'
  | 'telemovel'
  | 'numero_cc';

export type SortDir = 'ASC' | 'DESC';

export interface ListParticipantsParams {
  limit?: number;
  offset?: number;
  sortBy?: SortField;
  sortDir?: SortDir;
  search?: string;
}

export interface ListParticipantsResponse {
  participants: Participant[];
  total: number;
  limit: number;
  offset: number;
}

export interface ParticipantUpdateInput {
  nome: string;
  apelido: string;
  genero: string;
  dataNascimento: string;
  email?: string | null;
  telemovel?: string | null;
  numeroCc?: string | null;
  habilitacoesLiterarias?: string | null;
  situacaoLaboral?: string | null;
  escola?: string | null;
  eeNome?: string | null;
  eeTelemovel?: string | null;
  eeEmail?: string | null;
  eeNumeroCc?: string | null;
  morada?: string | null;
  localidade?: string | null;
  autorizacaoImagem: boolean;
  autorizacaoEe: boolean;
  estadoRegisto: string;
  /** Centro habitual; null clears it. */
  digitalCenterId?: number | null;
}

/** Staff registering by hand someone without a smartphone. */
export interface ParticipantCreateInput {
  nome: string;
  apelido: string;
  genero: string;
  dataNascimento: string;
  email?: string | null;
  telemovel?: string | null;
  numeroCc?: string | null;
  autorizacaoImagem: boolean;
  autorizacaoEe: boolean;
  digitalCenterId?: number | null;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/** Fetch a paginated, filtered, sorted list of participants. */
export function useParticipants(params: ListParticipantsParams = {}) {
  const {
    limit = 10,
    offset = 0,
    sortBy = 'nome',
    sortDir = 'ASC',
    search = '',
  } = params;

  const query = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    sortBy,
    sortDir,
    ...(search ? { search } : {}),
  });

  return useQuery({
    queryKey: ['participants', limit, offset, sortBy, sortDir, search],
    queryFn: () =>
      api.get<ListParticipantsResponse>(`/api/participants?${query}`),
    staleTime: 1000 * 30, // 30 s
  });
}

/** Fetch a single participant by ID. */
export function useParticipant(id: number | null) {
  return useQuery({
    queryKey: ['participant', id],
    queryFn: () => api.get<Participant>(`/api/participants/${id}`),
    enabled: id !== null,
  });
}

/** A participant's percurso: every visit, session, badge and equipment use. */
export function useParticipantPercurso(id: number | null) {
  return useQuery({
    queryKey: ['participant', id, 'percurso'],
    queryFn: () => api.get<Percurso>(`/api/participants/${id}/percurso`),
    enabled: id !== null,
    staleTime: 1000 * 30,
  });
}

/** Register a participant by hand. They start active. */
export function useCreateParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ParticipantCreateInput) =>
      api.post<Participant>('/api/participants', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
    },
  });
}

/** Update a participant. */
export function useUpdateParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ParticipantUpdateInput }) =>
      api.put<Participant>(`/api/participants/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
    },
  });
}

/**
 * Delete a participant. The API anonymises them: what identifies them is
 * erased, their history stays, tied to no one, for the figures.
 */
export function useDeleteParticipant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/participants/${id}`),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['participants'] });
      queryClient.removeQueries({ queryKey: ['participant', id] });
    },
  });
}
